// Shared bracket-tree pedigree data model.
// Used by both api/generate-cert.js (PDF) and src/App.js (PedigreeCertificate)
// so the two renderers stay structurally in sync. Framework-agnostic - no JSX,
// no React - plain CommonJS so it can be required from a Node serverless
// function as well as bundled into the CRA client.

const MAX_GENERATIONS = 5; // 0=subject, 1=parents, 2=grandparents, 3=great-grandparents, 4=2x great-grandparents

// Recursively builds a nested ancestor tree from rootDog, using get(id) to
// resolve each ancestor id to its row. Stops at maxGenerations deep. A branch
// with no known ancestor (missing id, or get(id) returns falsy) simply ends -
// no placeholder node is created, so callers naturally get a tree trimmed to
// the deepest generation actually on record for each lineage.
function buildPedigreeTree(rootDog, get, opts) {
  opts = opts || {};
  const sireField = opts.sireField || 'sireId';
  const damField = opts.damField || 'damId';
  const maxGen = opts.maxGenerations || MAX_GENERATIONS;

  function build(dogRow, gen) {
    if (!dogRow) return null;
    const node = Object.assign({}, dogRow, { sire: null, dam: null });
    if (gen < maxGen - 1) {
      const sireId = dogRow[sireField];
      const damId = dogRow[damField];
      node.sire = sireId ? build(get(sireId), gen + 1) : null;
      node.dam = damId ? build(get(damId), gen + 1) : null;
    }
    return node;
  }

  return build(rootDog, 0);
}

// Flattens the nested tree into a node list and an edge list, ready for
// rendering. Each node gets a fractional (x, y) in [0,1]: x is its generation
// column (0=subject .. maxGenerations-1=deepest column), y is its position
// within a canonical, evenly-subdivided binary layout for its generation - a
// parent's y is always exactly the midpoint of its two children's y, which is
// what makes the right-angle bracket connectors line up cleanly regardless of
// how deep any one branch goes.
function flattenPedigreeTree(root, maxGenerations, opts) {
  maxGenerations = maxGenerations || MAX_GENERATIONS;
  opts = opts || {};
  const nodes = [];
  const edges = [];

  function walk(node, gen, slotIndex) {
    if (!node) return null;
    const slots = gen === 0 ? 1 : Math.pow(2, gen);
    const y = gen === 0 ? 0.5 : (slotIndex + 0.5) / slots;
    const x = maxGenerations > 1 ? gen / (maxGenerations - 1) : 0;
    const sire = node.sire;
    const dam = node.dam;
    const rest = Object.assign({}, node);
    delete rest.sire;
    delete rest.dam;
    const entry = Object.assign(rest, { gen: gen, slotIndex: slotIndex, x: x, y: y });
    nodes.push(entry);
    if (sire) {
      const child = walk(sire, gen + 1, slotIndex * 2);
      if (child) edges.push({ from: entry, to: child, isSire: true });
    }
    if (dam) {
      const child = walk(dam, gen + 1, slotIndex * 2 + 1);
      if (child) edges.push({ from: entry, to: child, isSire: false });
    }
    return entry;
  }

  walk(root, 0, 0);

  // deepestGen reflects real, on-record ancestry only — computed before any
  // forceFull padding below, so "N-Generation Pedigree" labeling stays honest
  // even when the tree is padded out to a uniform depth for display.
  let realDeepestGen = 0;
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].gen > realDeepestGen) realDeepestGen = nodes[i].gen;
  }

  // Pads every canonical slot down to maxGenerations with an isPlaceholder
  // node (even below missing ancestors), so the certificate always renders a
  // uniform, full-depth tree — unknown ancestors show as a dim "(not on
  // record)" box instead of the branch simply ending early.
  if (opts.forceFull) {
    const bySlot = {};
    nodes.forEach(n => { bySlot[n.gen + ':' + n.slotIndex] = n; });
    for (let gen = 1; gen < maxGenerations; gen++) {
      const slots = Math.pow(2, gen);
      for (let slot = 0; slot < slots; slot++) {
        const key = gen + ':' + slot;
        if (!bySlot[key]) {
          const y = (slot + 0.5) / slots;
          const x = maxGenerations > 1 ? gen / (maxGenerations - 1) : 0;
          const placeholder = { gen: gen, slotIndex: slot, x: x, y: y, name: '', sex: null, isPlaceholder: true };
          bySlot[key] = placeholder;
          nodes.push(placeholder);
        }
      }
    }
    edges.length = 0;
    const root0 = bySlot['0:0'];
    for (let gen = 1; gen < maxGenerations; gen++) {
      const slots = Math.pow(2, gen);
      for (let slot = 0; slot < slots; slot++) {
        const child = bySlot[gen + ':' + slot];
        const parent = gen === 1 ? root0 : bySlot[(gen - 1) + ':' + Math.floor(slot / 2)];
        if (parent && child) edges.push({ from: parent, to: child, isSire: slot % 2 === 0 });
      }
    }
  }

  // deepestGen reflects what's actually rendered (padded, when forceFull is
  // on) — callers use it for column/loop bounds. realDeepestGen reflects only
  // what's genuinely on record — callers use it for "N-Generation Pedigree"
  // labeling so the label can't overclaim ancestry depth that's actually
  // just placeholder boxes.
  let deepestGen = 0;
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].gen > deepestGen) deepestGen = nodes[i].gen;
  }
  return { nodes: nodes, edges: edges, deepestGen: deepestGen, realDeepestGen: realDeepestGen };
}

module.exports = { buildPedigreeTree: buildPedigreeTree, flattenPedigreeTree: flattenPedigreeTree, MAX_GENERATIONS: MAX_GENERATIONS };
